/**
 * Audit Log Helper Functions
 * Centralized utility functions for audit log management
 */

// Action Categories with metadata
export const ACTION_CATEGORIES = {
  AUTHENTICATION: {
    name: 'Authentication',
    icon: 'fa-lock',
    color: '#4A90E2',
    actions: ['LOGIN', 'LOGOUT', 'PASSWORD_RESET', '2FA_ENABLE', '2FA_DISABLE', 'SESSION_EXPIRED']
  },
  USER_MANAGEMENT: {
    name: 'User Management',
    icon: 'fa-users',
    color: '#7B68EE',
    actions: ['CREATE_USER', 'UPDATE_USER', 'DELETE_USER', 'ACTIVATE_USER', 'DEACTIVATE_USER', 'IMPERSONATE_USER', 'EXPORT_USERS']
  },
  WORKSPACE: {
    name: 'Workspace Operations',
    icon: 'fa-briefcase',
    color: '#48C9B0',
    actions: ['CREATE_WORKSPACE', 'UPDATE_WORKSPACE', 'DELETE_WORKSPACE', 'SUSPEND_WORKSPACE', 'UNSUSPEND_WORKSPACE', 'EXPORT_WORKSPACES']
  },
  BILLING: {
    name: 'Billing & Payments',
    icon: 'fa-credit-card',
    color: '#F39C12',
    actions: ['PROCESS_PAYMENT', 'REFUND_PAYMENT', 'APPLY_CREDIT', 'CHANGE_PLAN', 'CANCEL_SUBSCRIPTION', 'REACTIVATE_SUBSCRIPTION']
  },
  AGENT: {
    name: 'Agent Management',
    icon: 'fa-robot',
    color: '#E74C3C',
    actions: ['START_AGENT', 'STOP_AGENT', 'RESTART_AGENT', 'UPDATE_AGENT_CONFIG', 'DELETE_AGENT', 'EXPORT_AGENTS']
  },
  DATA: {
    name: 'Data Operations',
    icon: 'fa-database',
    color: '#95A5A6',
    actions: ['EXPORT_DATA', 'IMPORT_DATA', 'BULK_UPDATE', 'BULK_DELETE', 'BACKUP_CREATE', 'RESTORE_BACKUP']
  },
  SYSTEM: {
    name: 'System Configuration',
    icon: 'fa-cogs',
    color: '#34495E',
    actions: ['UPDATE_SETTINGS', 'UPDATE_SECURITY_SETTINGS', 'CREATE_API_KEY', 'REVOKE_API_KEY', 'UPDATE_EMAIL_TEMPLATE']
  }
};

// Get category for an action
export const getActionCategory = (action) => {
  const upperAction = action?.toUpperCase();
  for (const [key, category] of Object.entries(ACTION_CATEGORIES)) {
    if (category.actions.includes(upperAction)) {
      return { key, ...category };
    }
  }
  return {
    key: 'OTHER',
    name: 'Other',
    icon: 'fa-circle',
    color: '#95A5A6'
  };
};

// Impact levels based on action type
export const getActionImpact = (action) => {
  const highImpact = ['DELETE', 'SUSPEND', 'CANCEL', 'REVOKE', 'DISABLE'];
  const mediumImpact = ['UPDATE', 'CHANGE', 'MODIFY', 'EDIT'];
  const lowImpact = ['VIEW', 'EXPORT', 'LOGIN', 'LOGOUT', 'READ'];
  
  const upperAction = action?.toUpperCase() || '';
  
  if (highImpact.some(keyword => upperAction.includes(keyword))) {
    return { level: 'HIGH', color: 'danger', icon: 'fa-exclamation-triangle' };
  }
  if (mediumImpact.some(keyword => upperAction.includes(keyword))) {
    return { level: 'MEDIUM', color: 'warning', icon: 'fa-exclamation-circle' };
  }
  if (lowImpact.some(keyword => upperAction.includes(keyword))) {
    return { level: 'LOW', color: 'info', icon: 'fa-info-circle' };
  }
  return { level: 'LOW', color: 'secondary', icon: 'fa-circle' };
};

// Format action name for display
export const formatActionName = (action) => {
  if (!action) return 'Unknown Action';
  
  // Replace underscores with spaces and capitalize
  return action
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, char => char.toUpperCase());
};

// Get action icon based on verb
export const getActionIcon = (action) => {
  const actionLower = action?.toLowerCase() || '';
  
  const iconMap = {
    create: 'fa-plus-circle',
    add: 'fa-plus-circle',
    update: 'fa-edit',
    edit: 'fa-edit',
    delete: 'fa-trash',
    remove: 'fa-trash',
    login: 'fa-sign-in',
    logout: 'fa-sign-out',
    export: 'fa-download',
    import: 'fa-upload',
    view: 'fa-eye',
    start: 'fa-play',
    stop: 'fa-stop',
    restart: 'fa-sync',
    suspend: 'fa-pause',
    activate: 'fa-check-circle',
    deactivate: 'fa-times-circle',
    process: 'fa-cog',
    send: 'fa-paper-plane',
    approve: 'fa-check',
    reject: 'fa-times',
    cancel: 'fa-ban'
  };
  
  for (const [key, icon] of Object.entries(iconMap)) {
    if (actionLower.includes(key)) return icon;
  }
  
  return 'fa-circle';
};

// Get status badge configuration
export const getStatusBadge = (success) => {
  if (success === true || success === 'true') {
    return {
      text: 'Success',
      color: 'success',
      icon: 'fa-check-circle'
    };
  }
  if (success === false || success === 'false') {
    return {
      text: 'Failed',
      color: 'danger',
      icon: 'fa-times-circle'
    };
  }
  return {
    text: 'Complete',
    color: 'info',
    icon: 'fa-check'
  };
};

// Format timestamp to relative time
export const formatRelativeTime = (timestamp) => {
  if (!timestamp) return 'Unknown';
  
  try {
    const date = new Date(timestamp);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }
    
    const now = new Date();
    const diffMs = now - date;
    
    // If date is in the future
    if (diffMs < 0) {
      return 'Future date';
    }
    
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffSecs < 60) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString();
  } catch (error) {
    return 'Invalid date';
  }
};

// Format target/resource display
export const formatTarget = (resource, resourceId, metadata) => {
  if (!resource) return 'Unknown';
  
  const resourceMap = {
    users: 'User',
    user: 'User',
    workspaces: 'Workspace',
    workspace: 'Workspace',
    agents: 'Agent',
    agent: 'Agent',
    payments: 'Payment',
    payment: 'Payment',
    subscriptions: 'Subscription',
    subscription: 'Subscription',
    settings: 'Settings',
    security: 'Security',
    'email-templates': 'Email Template',
    'api-keys': 'API Key'
  };
  
  const displayName = resourceMap[resource.toLowerCase()] || resource;
  
  // Try to get more specific info from metadata
  if (metadata) {
    if (metadata.userName) return `${displayName}: ${metadata.userName}`;
    if (metadata.workspaceName) return `${displayName}: ${metadata.workspaceName}`;
    if (metadata.agentName) return `${displayName}: ${metadata.agentName}`;
    if (metadata.email) return `${displayName}: ${metadata.email}`;
    if (metadata.name) return `${displayName}: ${metadata.name}`;
  }
  
  if (resourceId) return `${displayName} #${resourceId.toString().slice(-6)}`;
  
  return displayName;
};

// Parse and format change details
export const formatChangeDetails = (changes, requestBody, metadata) => {
  const details = [];
  
  // Parse changes object
  if (changes && typeof changes === 'object') {
    for (const [key, value] of Object.entries(changes)) {
      if (value && typeof value === 'object' && value.from !== undefined && value.to !== undefined) {
        details.push({
          field: formatFieldName(key),
          from: formatValue(value.from),
          to: formatValue(value.to),
          type: 'change'
        });
      } else {
        details.push({
          field: formatFieldName(key),
          value: formatValue(value),
          type: 'set'
        });
      }
    }
  }
  
  // Add metadata info
  if (metadata && typeof metadata === 'object') {
    for (const [key, value] of Object.entries(metadata)) {
      if (!['requestId', 'sessionId', 'timestamp'].includes(key)) {
        details.push({
          field: formatFieldName(key),
          value: formatValue(value),
          type: 'info'
        });
      }
    }
  }
  
  return details;
};

// Format field names for display
const formatFieldName = (field) => {
  if (!field) return 'Unknown Field';
  
  // Special cases
  const fieldMap = {
    firstName: 'First Name',
    lastName: 'Last Name',
    email: 'Email',
    role: 'Role',
    status: 'Status',
    isActive: 'Active',
    createdAt: 'Created Date',
    updatedAt: 'Updated Date',
    planName: 'Plan',
    amount: 'Amount',
    workspaceId: 'Workspace',
    userId: 'User',
    agentId: 'Agent'
  };
  
  return fieldMap[field] || field
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
};

// Format values for display
const formatValue = (value) => {
  if (value === null || value === undefined) return 'None';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return value.toLocaleString();
  if (value instanceof Date) return value.toLocaleDateString();
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
};

// Quick filter configurations
export const QUICK_FILTERS = [
  {
    id: 'all',
    label: 'All Actions',
    icon: 'fa-list',
    filter: {}
  },
  {
    id: 'failed',
    label: 'Failed Actions',
    icon: 'fa-times-circle',
    color: 'danger',
    filter: { success: false }
  },
  {
    id: 'critical',
    label: 'Critical Changes',
    icon: 'fa-exclamation-triangle',
    color: 'warning',
    filter: { impact: 'HIGH' }
  },
  {
    id: 'today',
    label: "Today's Activity",
    icon: 'fa-calendar-day',
    color: 'primary',
    filter: { dateRange: 'today' }
  },
  {
    id: 'my-actions',
    label: 'My Actions',
    icon: 'fa-user',
    color: 'info',
    filter: { adminId: 'current' }
  },
  {
    id: 'security',
    label: 'Security Events',
    icon: 'fa-shield-alt',
    color: 'success',
    filter: { category: 'AUTHENTICATION' }
  }
];

// Get activity statistics from logs
export const calculateActivityStats = (logs) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  const stats = {
    total: logs.length,
    today: 0,
    failed: 0,
    critical: 0,
    byCategory: {},
    byUser: {},
    peakHour: null,
    mostActiveAdmin: null
  };
  
  const hourlyActivity = {};
  
  logs.forEach(log => {
    const logDate = new Date(log.timestamp || log.createdAt);
    
    // Count today's actions
    if (logDate >= today) {
      stats.today++;
      
      // Track hourly activity
      const hour = logDate.getHours();
      hourlyActivity[hour] = (hourlyActivity[hour] || 0) + 1;
    }
    
    // Count failed actions
    if (!log.success) stats.failed++;
    
    // Count critical actions
    const impact = getActionImpact(log.action);
    if (impact.level === 'HIGH') stats.critical++;
    
    // Count by category
    const category = getActionCategory(log.action);
    stats.byCategory[category.key] = (stats.byCategory[category.key] || 0) + 1;
    
    // Count by user
    const userName = log.user?.name || log.user?.email || 'Unknown';
    stats.byUser[userName] = (stats.byUser[userName] || 0) + 1;
  });
  
  // Find peak hour
  const peakHourEntry = Object.entries(hourlyActivity).sort((a, b) => b[1] - a[1])[0];
  if (peakHourEntry) {
    const hour = parseInt(peakHourEntry[0]);
    stats.peakHour = {
      hour: `${hour}:00 - ${hour + 1}:00`,
      count: peakHourEntry[1]
    };
  }
  
  // Find most active admin
  const mostActiveEntry = Object.entries(stats.byUser).sort((a, b) => b[1] - a[1])[0];
  if (mostActiveEntry) {
    stats.mostActiveAdmin = {
      name: mostActiveEntry[0],
      count: mostActiveEntry[1]
    };
  }
  
  return stats;
};

// Export functions for generating reports
export const exportAuditLogs = (logs, format = 'csv') => {
  if (format === 'csv') {
    const headers = ['Date', 'Admin', 'Action', 'Target', 'Status', 'Impact'];
    const rows = logs.map(log => [
      log.createdAt ? 
        new Date(log.createdAt).toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric'
        }) : 
        new Date().toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric'
        }),
      log.adminId ? `${log.adminId.firstName || 'Unknown'} ${log.adminId.lastName || ''}` : 'System',
      formatActionName(log.action),
      formatTarget(log.resource, log.resourceId, log.metadata),
      getStatusBadge(log.success).text,
      getActionImpact(log.action).level
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  }
};